import type { ActionFunction } from '~/types/route-args';
import { data as routerData } from 'react-router';
import type {
    CreateEnquiryMutation,
    CreateEnquiryMutationVariables,
} from 'generated/graphql';
import {
    EnquirerType,
    EnquiryChannel,
    EnquiryProjectKind,
} from 'generated/graphql';
import { CreateEnquiryDocument } from 'generated/documents/CreateEnquiryDocument';
import { assertResponse } from '~/modules/root/graphql/assertResponse.server';
import { SuccessResponse } from '~/modules/root/graphql/responses';
import { getSession } from '~/modules/root/util/session.server';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { DEFAULT_PUBLIC_USER } from '~/routes/remix-api.user.public-user';
import type { SocialLoginProvider } from '~/modules/user/component/social-login/type';
import { isApolloError } from '@apollo/client';
import {
    allowSubmitFrom,
    chargeFormSubmit,
    formHasCapacity,
} from '~/modules/enquiry/util/embed-protection.server';
import { clientIp } from '~/modules/enquiry/util/client-ip.server';
import {
    CTA_SOURCE_FIELD,
    parseCtaSource,
} from '~/modules-rocco/booking/utils/cta-source';
import {
    DEFAULT_DECLINED_MESSAGE,
    readEnquirySubmitOutcome,
    type EnquirySubmitOutcome,
} from '~/modules/enquiry/domain/enquiry-outcome';

export type SubmitActionResponse =
    | SuccessResponse<CreateEnquiryMutation['createEnquiry']>
    | SubmitSuccessResponse
    | CustomErrorResponse
    | DeclinedErrorResponse
    | SubmitErrorResponse;

/**
 * A created enquiry, plus what the server decided about it: where it routed
 * and what the form shows next. `outcome` values are null when the API does
 * not report them.
 */
export interface SubmitSuccessResponse {
    success: true;
    data: CreateEnquiryMutation['createEnquiry'];
    outcome: EnquirySubmitOutcome;
}

interface CustomErrorResponse {
    error: boolean;
    publicUser: PublicUser;
}

/**
 * A routing rule declined the enquiry and the form asked to show that itself
 * (`declineHandling=inline`) rather than as a toast.
 */
export interface DeclinedErrorResponse {
    error: true;
    declined: true;
    declinedMessage: string;
}

/**
 * Why a website embed's submit failed, for the form to show beside its button.
 * An embed runs in a third-party iframe, where the browser drops the session
 * cookie a flashed toast rides on, so every embed failure comes back as data.
 */
export interface SubmitErrorResponse {
    error: true;
    message: string;
}

const RATE_LIMITED_MESSAGE =
    'Too many enquiries from this connection. Please try again in a minute.';
const FORM_BUSY_MESSAGE =
    'This form is taking too many enquiries right now. Please try again later.';
const SEND_FAILED_MESSAGE = 'Send enquiry failed, please try again later';

/** The Remix action route the enquiry form posts to. */
export const ENQUIRY_SUBMIT_API_ROUTE = '/remix-api/enquiry/submit';

/**
 * Parse a JSON array of positive integer ids from a form field (used by the
 * bulk-enquire flow), returning null when the field is absent or malformed so
 * the caller can fall back to the single-id fields.
 */
const parseIdArray = (raw: FormDataEntryValue | null): number[] | null => {
    if (!raw) return null;
    try {
        const parsed: unknown = JSON.parse(raw.toString());
        if (!Array.isArray(parsed)) return null;
        const ids = parsed
            .map(Number)
            .filter((id) => Number.isInteger(id) && id > 0);
        return ids.length > 0 ? ids : null;
    } catch {
        return null;
    }
};

const parsePositiveInt = (
    value: FormDataEntryValue | null,
): number | undefined => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 ? parsed : undefined;
};

const parseEnquirerType = (
    value: FormDataEntryValue | null,
): EnquirerType | undefined => {
    switch (value) {
        case EnquirerType.Homeowner:
        case EnquirerType.Specifier:
        case EnquirerType.SupplierTrade:
            return value;
        default:
            return undefined;
    }
};

const parseProjectKind = (
    value: FormDataEntryValue | null,
): EnquiryProjectKind | undefined => {
    switch (value) {
        case EnquiryProjectKind.Residential:
        case EnquiryProjectKind.Commercial:
            return value;
        default:
            return undefined;
    }
};

/**
 * A JSON object of string values posted as one form field (custom field
 * answers, UTM params). Anything else is dropped rather than rejected so a
 * malformed extra never blocks the enquiry itself.
 */
const parseKeyValues = (
    raw: FormDataEntryValue | null,
): { Key: string; Value: string }[] | undefined => {
    if (!raw) return undefined;
    try {
        const parsed: unknown = JSON.parse(raw.toString());
        if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
            return undefined;
        }
        const pairs = Object.entries(parsed as Record<string, unknown>)
            .filter(
                (entry): entry is [string, string] =>
                    typeof entry[1] === 'string' && entry[0].trim() !== '',
            )
            .map(([Key, Value]) => ({ Key: Key.slice(0, 60), Value }));
        return pairs.length > 0 ? pairs : undefined;
    } catch {
        return undefined;
    }
};

const parseSourceUrl = (raw: FormDataEntryValue | null): string | undefined => {
    const value = raw?.toString().trim() ?? '';
    if (value === '' || value.length > 2000) return undefined;
    try {
        const url = new URL(value);
        return url.protocol === 'https:' || url.protocol === 'http:'
            ? value
            : undefined;
    } catch {
        return undefined;
    }
};

const parseChannel = (
    value: FormDataEntryValue | null,
): EnquiryChannel | undefined => {
    switch (value) {
        case 'website_embed':
            return EnquiryChannel.WebsiteEmbed;
        case 'archipro':
            return EnquiryChannel.Archipro;
        // The API only honours these for a signed-in member.
        case 'book_meeting_member':
            return EnquiryChannel.BookMeetingMember;
        case 'book_meeting_company':
            return EnquiryChannel.BookMeetingCompany;
        default:
            return undefined;
    }
};

/**
 * A submit that failed before or inside the API. An embed gets the message as
 * data to show inline; ArchiPro pages keep the flashed toast.
 */
const failSubmit = async (
    session: Awaited<ReturnType<typeof getSession>>,
    {
        message,
        status,
        inline,
    }: { message: string; status?: number; inline: boolean },
) => {
    if (inline) {
        return routerData<SubmitErrorResponse>(
            { error: true, message },
            status ? { status } : undefined,
        );
    }
    session.flashToastMessage({ message, type: 'error' });
    const cookie = await session.commit();
    return routerData(
        { error: true },
        {
            ...(status && { status }),
            ...(cookie && { headers: await session.getHeaders() }),
        },
    );
};

/**
 * Whether the API took this as a submission through a Website form, which it
 * only does after the form's captcha passed. The server decides the channel
 * from the form, so a client that leaves out the embed channel is still
 * counted. A decline has no enquiry to read it from, and counts when the post
 * came from the embed.
 */
const acceptedAsWebsiteSubmission = (
    response: CreateEnquiryResultData,
    claimedEmbed: boolean,
): boolean =>
    response.Declined
        ? claimedEmbed
        : (response.Enquiries ?? []).some(
              (enquiry) => enquiry?.Channel === EnquiryChannel.WebsiteEmbed,
          );

type CreateEnquiryResultData = Extract<
    NonNullable<CreateEnquiryMutation['createEnquiry']>,
    { __typename: 'CreateEnquiryResult' }
>;

export const action: ActionFunction = async ({ request, context }) => {
    const { client, clearUserCache } = context;
    const form = await request.formData();

    // Extract files from form data
    const files = [];
    for (const [key, value] of form.entries()) {
        if (key.startsWith('Attachments[') && value instanceof File) {
            files.push(value);
        }
    }

    const buildHubId = form.get('buildHubId');
    const buildHubFieldSelection = form
        .get('buildHubFieldSelection')
        ?.toString();

    // Bulk enquiry posts a JSON array of ids; a single-item enquiry posts one.
    const siteTreeIds = parseIdArray(form.get('siteTreeIds'));
    const channel = parseChannel(form.get('channel'));
    const isWebsiteEmbed = channel === EnquiryChannel.WebsiteEmbed;

    const submission = {
        Name: form.get('name')?.toString()?.trim(),
        CreatedAt: Date.now(),
        Location: form.get('suburbPostcode')?.toString().trim(),
        locationResponse: form.get('locationResponse')?.toString().trim(),
        Email: form.get('email')?.toString().trim(),
        Message: form.get('message')?.toString().trim(),
        Phone: form.get('phoneNumber')?.toString().trim(),
        ShareContactDetails: form.get('shareDetails') === 'true',
        SiteTreeIDs: siteTreeIds ?? [Number(form.get('siteTreeId'))],
        // Create a new account if the user does not have one. A website embed
        // never does: its visitors are on the professional's own site, and the
        // API records their enquiry as a guest lead.
        AutoCreateMember: !isWebsiteEmbed,
        Branch: Number(form.get('branchId')),
        BuildHubID: buildHubId ? Number(buildHubId) : undefined,
        BuildHubFieldSelection: buildHubFieldSelection || undefined,
        // Business Manager Forms: which configured form (and option) the
        // visitor used, so recipients route and booking requests are recorded.
        EnquiryFormID: parsePositiveInt(form.get('enquiryFormId')),
        EnquiryFormTypeID: parsePositiveInt(form.get('enquiryFormTypeId')),
        EnquirerType: parseEnquirerType(form.get('enquirerType')),
        EnquiryProjectKind: parseProjectKind(form.get('projectKind')),
        RequestBooking: form.get('requestBooking') === 'true' || undefined,
        UserRegion: form.get('suburbPostcode')?.toString().trim() || undefined,
        Channel: channel,
        // Website forms: custom question answers and where the embed was hosted.
        FieldAnswers: parseKeyValues(form.get('fieldAnswers')),
        SourceUrl: parseSourceUrl(form.get('sourceUrl')),
        SourceParams: parseKeyValues(form.get('sourceParams')),
        // Turnstile token from the embed page; the API requires and verifies
        // it for every Website form, whatever channel is claimed here.
        CaptchaToken: form.get('captchaToken')?.toString() || undefined,
        // The Book a meeting button a booking gate enquiry came from; a meeting
        // request's server event reports it as Source.
        CtaSource: parseCtaSource(form.get(CTA_SOURCE_FIELD)),
        // Add files to the submission
        Attachments: files,
        // A bulk enquiry must fan out per item owner, so it deliberately sends no
        // targets: with TargetProfessionals empty the backend groups SiteTreeIDs
        // by each item's professional and sends every supplier only their own
        // items. Naming targets instead broadcasts the WHOLE selection to each of
        // them, so supplier A would see supplier B's items.
        TargetProfessionals: siteTreeIds
            ? []
            : form.get('targetProfessionalId')
              ? [Number(form.get('targetProfessionalId'))]
              : [],
    };

    const session = await getSession(request);
    // Embed failures are shown by the form itself (see SubmitErrorResponse).
    const inline = isWebsiteEmbed;

    // A form submission is limited per sender before the API is asked, and a
    // website form refuses once its hourly ceiling is spent. The ceiling is
    // only charged for submissions the API accepted past the captcha.
    const formId = submission.EnquiryFormID;
    if (formId && !allowSubmitFrom(formId, clientIp(request))) {
        return failSubmit(session, {
            message: RATE_LIMITED_MESSAGE,
            status: 429,
            inline,
        });
    }
    if (formId && !formHasCapacity(formId)) {
        return failSubmit(session, {
            message: FORM_BUSY_MESSAGE,
            status: 429,
            inline,
        });
    }

    try {
        const { data } = await client.mutate<
            CreateEnquiryMutation,
            CreateEnquiryMutationVariables
        >({
            mutation: CreateEnquiryDocument,
            variables: {
                EnquiryInput: submission,
            },
            context: {
                fetchOptions: {
                    useUpload: true,
                },
            },
        });

        await clearUserCache();

        const handleAuthenticationException = (
            createEnquiry: CreateEnquiryMutation['createEnquiry'],
        ) => {
            if (createEnquiry?.__typename === 'AuthenticationException') {
                // The API takes website embeds as guest enquiries, so this
                // is never expected there. Signing in cannot work inside a
                // third-party iframe, and the account holder's details must
                // not reach someone typing an email into another site's
                // form, so it fails as a plain send error.
                if (isWebsiteEmbed) {
                    throw new Error(
                        'A website embed submission asked for a sign-in.',
                    );
                }
                let publicUser = { ...DEFAULT_PUBLIC_USER, type: 'new' };
                if (createEnquiry.ExpectedMember) {
                    const existingUser = createEnquiry.ExpectedMember;
                    publicUser = {
                        ...DEFAULT_PUBLIC_USER,
                        firstName: existingUser.FirstName,
                        lastName: existingUser.LastName,
                        profileImageLink: existingUser.ProfileImageLink,
                        archiproLoginOption:
                            existingUser.LoginOptions.includes('ArchiPro'),
                        socialLoginOptions: existingUser.LoginOptions.filter(
                            (option) => option !== 'ArchiPro',
                        ) as SocialLoginProvider[],
                        type: 'existing',
                    };
                }
                return {
                    error: true,
                    publicUser,
                };
            }
            return createEnquiry;
        };

        const response = await assertResponse(
            context,
            data?.createEnquiry,
            undefined,
            handleAuthenticationException,
        );

        const isCreateResult =
            !!response &&
            '__typename' in response &&
            response.__typename === 'CreateEnquiryResult';

        if (
            formId &&
            isCreateResult &&
            acceptedAsWebsiteSubmission(response, isWebsiteEmbed)
        ) {
            chargeFormSubmit(formId);
        }

        // A routing rule declined it server-side (the form normally catches
        // this before submit; stale embeds can still get here). Surface the
        // rule's message rather than a success screen: as data when the form
        // shows declines itself, otherwise as a toast.
        if (isCreateResult && response.Declined) {
            const declinedMessage =
                response.DeclinedMessage ?? DEFAULT_DECLINED_MESSAGE;
            if (inline || form.get('declineHandling') === 'inline') {
                return routerData<DeclinedErrorResponse>({
                    error: true,
                    declined: true,
                    declinedMessage,
                });
            }
            session.flashToastMessage({
                message: declinedMessage,
                type: 'error',
            });
            const declinedCookie = await session.commit();
            return routerData(
                { error: true },
                {
                    ...(declinedCookie && {
                        headers: await session.getHeaders(),
                    }),
                },
            );
        }

        const cookie = await session.commit();

        if (response && 'error' in response && response.error) {
            return routerData(response, {
                ...(cookie && { headers: await session.getHeaders() }),
            });
        }
        // Routing results ride beside the enquiry so the form can follow
        // the server's decision (a booking hand-off, the form's thank-you)
        // instead of predicting it.
        if (isCreateResult) {
            return routerData<SubmitSuccessResponse>(
                {
                    success: true,
                    data: response,
                    outcome: readEnquirySubmitOutcome(response),
                },
                { ...(cookie && { headers: await session.getHeaders() }) },
            );
        }
        return new SuccessResponse(response, {
            ...(cookie && { headers: await session.getHeaders() }),
        });
    } catch (e) {
        let errorMsg = SEND_FAILED_MESSAGE;
        if (e instanceof Error && isApolloError(e)) {
            errorMsg = e.graphQLErrors[0]?.message ?? '';
        }

        return failSubmit(session, {
            message: inline ? errorMsg || SEND_FAILED_MESSAGE : errorMsg,
            inline,
        });
    }
};
